import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, Tab, Grid } from '../../../base';
import {
    CardWrapper,
    StyledButton,
    StyledContentContainer,
} from './InterviewDetails.styles';
import InterviewQuestions from '../InterviewQuestions';
import InterviewTrail from '../InterviewTrail';
import CommentBox from '../CommentBox';
import PargodaModal from '../../PargodaModal';
import SendMessageBox from '../SendMessageBox';
import { IJobApplication } from '../../../common';
import ChatTab from '../ChatTab';
import EmailTab from '../EmailTab';
import { SendEmailBox } from '../EmailTab/EmailModals';

interface Props {
    jobApplication: IJobApplication;
    isLegacy?: boolean;
}

const InterviewDetails = (props: Props) => {
    const { jobApplication, isLegacy = false } = props;

    // Find the current step by ID, not by hardcoded index
    const currentStep = jobApplication.steps?.find(
        (step) => step.id === jobApplication.currentStep
    );

    const currentInterview =
        currentStep?.interview?.find(
            (interview) =>
                Number(interview.job_application) === Number(jobApplication.id)
        ) ?? currentStep?.interview?.[0];

    const interviewQuestions = currentInterview?.interview_questions;

    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleTabChange = (
        _event: React.SyntheticEvent,
        newValue: number
    ) => {
        setActiveTab(newValue);
    };

    const handleOpenModel = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 0:
                return <InterviewTrail jobApplication={jobApplication} />;
            case 1:
                return (
                    <InterviewQuestions
                        key={jobApplication.id}
                        interviewQuestions={interviewQuestions}
                    />
                );
            case 2:
                return <ChatTab messages={jobApplication.chat} />;
            case 3:
                return <EmailTab emails={jobApplication.emails} />;
            default:
                return null;
        }
    };

    const getModalTitle = () => {
        switch (activeTab) {
            case 0:
                return t('trail.addComment');
            case 2:
                return t('jobApplicationPage.sendMessage');
            case 3:
                return t('jobApplicationPage.sendEmail');
            default:
                return '';
        }
    };

    const getButtonText = () => {
        switch (activeTab) {
            case 0:
                return t('trail.addComment');
            case 2:
                return t('jobApplicationPage.sendMessage');
            case 3:
                return t('jobApplicationPage.sendEmail');
            default:
                return '';
        }
    };

    const getModelContent = () => {
        switch (activeTab) {
            case 0:
                return (
                    <CommentBox
                        comments={jobApplication.comments}
                        displayComments={false}
                        withCard={false}
                        onSave={handleCloseModal}
                        isDisabled={isLegacy}
                        rows={8}
                    />
                );
            case 2:
                return <SendMessageBox onSave={handleCloseModal} />;
            case 3:
                return (
                    <SendEmailBox
                        jobApplication={jobApplication}
                        onSave={handleCloseModal}
                    />
                );
            default:
                return null;
        }
    };

    const getModalSx = () => {
        if (activeTab === 3) {
            return { width: '800px', maxWidth: '90%' };
        }
        if (activeTab === 0) {
            return { width: '560px', maxWidth: '90%' };
        }
        return { width: '400px', maxWidth: '90%' };
    };

    return (
        <CardWrapper>
            <Grid container justifyContent="space-between" alignItems="center">
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label={t('jobApplicationPage.interviewTrail')} />
                    <Tab label={t('jobApplicationPage.interviewQuestions')} />
                    <Tab label={t('jobApplicationPage.chat')} disabled />
                    <Tab label={t('jobApplicationPage.email')} disabled />
                </Tabs>
                {activeTab !== 1 && (
                    <StyledButton
                        variant="outlined"
                        color="secondary"
                        onClick={handleOpenModel}
                        disabled={
                            isLegacy && (activeTab === 0 || activeTab === 3)
                        }
                    >
                        {getButtonText()}
                    </StyledButton>
                )}
            </Grid>

            <StyledContentContainer>
                {renderTabContent()}
            </StyledContentContainer>

            <PargodaModal
                modalTitle={getModalTitle()}
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                sx={getModalSx()}
            >
                {getModelContent()}
            </PargodaModal>
        </CardWrapper>
    );
};

export default InterviewDetails;
